import React, { forwardRef, useImperativeHandle, useMemo, useState, useCallback } from 'react';
import { Box, Typography, TextField, Stack, Button, Tooltip } from '@mui/material';
import { Fullscreen as FullscreenIcon } from '@mui/icons-material';
import MSAModulePage from './MSAModulePage';
import GuidedEntry from '../GuidedEntry';
import CategoricalGrid from '../CategoricalGrid';

// ---------------------------------------------------------------------------
// AttributeAnalyticModule — signal-detection attribute study.
//
// Each part has a true continuous REFERENCE value; every operator judges the
// part Pass/Fail. The engine scores how well the Pass/Fail calls recover the
// true conforming / defective state implied by the spec limits.
//
// Entry is split in two to avoid retyping the reference value once per operator:
//   (a) a compact Parts table   — one Reference Value per part,
//   (b) a ratings CategoricalGrid — one Rating per (part × operator).
// buildInputs() joins them into the flat `assessments` payload the engine wants.
// ---------------------------------------------------------------------------

const inputSx = {
    '& .MuiOutlinedInput-root': {
        color: 'rgba(255,255,255,0.9)',
        '& fieldset': { borderColor: 'rgba(255,255,255,0.23)' },
        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
        '&.Mui-focused fieldset': { borderColor: 'rgba(144,202,249,0.7)' },
    },
    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.6)' },
    '& .MuiInputLabel-root.Mui-focused': { color: 'rgba(144,202,249,0.9)' },
};

const sectionLabelSx = {
    color: 'rgba(255,255,255,0.6)',
    fontSize: '0.7rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
};

const DEFAULT_PARTS = 5;
const DEFAULT_OPERATORS = 2;
const MAX_PARTS = 100;
const MAX_OPERATORS = 20;

function parseCategories(raw) {
    const list = String(raw || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    return list.length ? list : ['Pass', 'Fail'];
}

function clampInt(raw, min, max, fallback) {
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n)) return fallback;
    return Math.max(min, Math.min(max, n));
}

export const AttributeAnalyticInputs = forwardRef(function AttributeAnalyticInputs({ disabled }, ref) {
    const [usl, setUsl] = useState('');
    const [lsl, setLsl] = useState('');
    const [categoriesRaw, setCategoriesRaw] = useState('Pass, Fail');
    const [numPartsRaw, setNumPartsRaw] = useState(String(DEFAULT_PARTS));
    const [numOperatorsRaw, setNumOperatorsRaw] = useState(String(DEFAULT_OPERATORS));

    // Reference value per part, keyed by 1-based part index → string.
    const [partRefs, setPartRefs] = useState({});
    // Rating per (part, operator), keyed `${part}|${op}` → category label string.
    const [ratings, setRatings] = useState({});

    // Guided (fullscreen sequential) rating entry
    const [guidedMode, setGuidedMode]     = useState(false);
    const [guidedSlots, setGuidedSlots]   = useState([]);
    const [guidedValues, setGuidedValues] = useState([]); // parallel to guidedSlots

    const numParts = clampInt(numPartsRaw, 1, MAX_PARTS, DEFAULT_PARTS);
    const numOperators = clampInt(numOperatorsRaw, 2, MAX_OPERATORS, DEFAULT_OPERATORS);
    const categories = useMemo(() => parseCategories(categoriesRaw), [categoriesRaw]);

    const operators = useMemo(
        () => Array.from({ length: numOperators }, (_, i) => `Op${i + 1}`),
        [numOperators],
    );

    // --- Parts table (Reference Value per part) --------------------------------
    const partColumns = useMemo(
        () => [
            { key: 'part', label: 'Part', type: 'readonly', width: 80 },
            { key: 'referenceValue', label: 'Reference Value', type: 'number', width: 160, align: 'right', placeholder: '0.000' },
        ],
        [],
    );

    const partRows = useMemo(
        () => Array.from({ length: numParts }, (_, i) => ({
            part: `Part ${i + 1}`,
            referenceValue: partRefs[i + 1] ?? '',
        })),
        [numParts, partRefs],
    );

    const handlePartCellChange = (rowIndex, key, value) => {
        if (key !== 'referenceValue') return;
        const partNo = rowIndex + 1;
        setPartRefs((prev) => ({ ...prev, [partNo]: value }));
    };

    // --- Ratings grid (part × operator) ----------------------------------------
    const ratingColumns = useMemo(
        () => [
            { key: 'part', label: 'Part', type: 'readonly', width: 80 },
            { key: 'operator', label: 'Operator', type: 'readonly', width: 90 },
            { key: 'rating', label: 'Rating', type: 'category', categories, align: 'left' },
        ],
        [categories],
    );

    const ratingRows = useMemo(() => {
        const out = [];
        for (let p = 1; p <= numParts; p += 1) {
            for (let o = 0; o < numOperators; o += 1) {
                const op = operators[o];
                out.push({
                    _part: p,
                    _op: op,
                    part: `Part ${p}`,
                    operator: op,
                    rating: ratings[`${p}|${op}`] ?? null,
                });
            }
        }
        return out;
    }, [numParts, numOperators, operators, ratings]);

    const handleRatingCellChange = (rowIndex, key, value) => {
        if (key !== 'rating') return;
        const r = ratingRows[rowIndex];
        if (!r) return;
        setRatings((prev) => ({ ...prev, [`${r._part}|${r._op}`]: value }));
    };

    // --- Guided entry (categorical) ---------------------------------------------
    const gridCategories = categories.length >= 2 ? categories : ['Pass', 'Fail'];

    const handleEnterGuided = useCallback(() => {
        const slots = ratingRows.map((r) => ({ label: `${r.part} / ${r.operator}`, key: `${r._part}|${r._op}` }));
        setGuidedSlots(slots);
        setGuidedValues(ratingRows.map((r) => r.rating ?? null));
        setGuidedMode(true);
    }, [ratingRows]);

    const handleGuidedSubmitValue = useCallback((cat, idx) => {
        setGuidedValues((prev) => { const next = prev.slice(); next[idx] = cat; return next; });
    }, []);

    const handleExitGuided = useCallback(() => {
        setRatings((prev) => {
            const next = { ...prev };
            guidedSlots.forEach((slot, i) => {
                const v = guidedValues[i];
                if (v !== null && v !== undefined) next[slot.key] = v;
            });
            return next;
        });
        setGuidedMode(false);
    }, [guidedSlots, guidedValues]);

    // Prune ratings orphaned by a category-label change so the grid's appearance
    // matches state — but only once the user has a valid (>=2) set, so mid-typing
    // (a transient single/empty value, where the grid falls back to Pass/Fail)
    // doesn't wipe entered ratings.
    const handleCategoriesChange = (raw) => {
        setCategoriesRaw(raw);
        const parsed = parseCategories(raw);
        if (parsed.length >= 2) {
            const valid = new Set(parsed);
            setRatings((prev) => {
                const next = {};
                Object.entries(prev).forEach(([k, v]) => { if (valid.has(v)) next[k] = v; });
                return next;
            });
        }
    };

    useImperativeHandle(ref, () => ({
        buildInputs() {
            if (categories.length < 2) {
                throw new Error('Enter at least two rating categories (comma-separated), e.g. "Pass, Fail".');
            }
            const uslProvided = String(usl).trim() !== '';
            const lslProvided = String(lsl).trim() !== '';
            if (!uslProvided && !lslProvided) {
                throw new Error('Enter at least one spec limit (USL or LSL).');
            }

            const uslNum = uslProvided ? Number(usl) : undefined;
            const lslNum = lslProvided ? Number(lsl) : undefined;
            if (uslProvided && !Number.isFinite(uslNum)) {
                throw new Error('USL must be a valid number.');
            }
            if (lslProvided && !Number.isFinite(lslNum)) {
                throw new Error('LSL must be a valid number.');
            }
            if (uslProvided && lslProvided && !(lslNum < uslNum)) {
                throw new Error('LSL must be less than USL.');
            }

            // Every part must have a finite reference value.
            const refByPart = {};
            for (let p = 1; p <= numParts; p += 1) {
                const raw = partRefs[p];
                if (raw === undefined || String(raw).trim() === '') {
                    throw new Error(`Enter a reference value for Part ${p}.`);
                }
                const num = Number(raw);
                if (!Number.isFinite(num)) {
                    throw new Error(`Reference value for Part ${p} must be a valid number.`);
                }
                refByPart[p] = num;
            }

            // Every (part × operator) row must be rated with a CURRENT category —
            // a rating left over from a since-renamed category counts as unrated.
            const catSet = new Set(categories);
            const assessments = [];
            for (let p = 1; p <= numParts; p += 1) {
                for (let o = 0; o < numOperators; o += 1) {
                    const op = operators[o];
                    const rating = ratings[`${p}|${op}`];
                    if (rating === undefined || rating === null || String(rating).trim() === '' || !catSet.has(rating)) {
                        throw new Error(`Rate Part ${p} for ${op}.`);
                    }
                    // `part` is ignored by the engine + validator but persists in the
                    // stored input, making history reload loss-free (see hydrate()).
                    assessments.push({ operator: op, referenceValue: refByPart[p], rating, part: p });
                }
            }

            const payload = { assessments, categories };
            if (uslProvided) payload.usl = uslNum;
            if (lslProvided) payload.lsl = lslNum;
            return payload;
        },
        // Re-populate from a persisted engine input (history reload). The persisted
        // assessments are flat { operator, referenceValue, rating } — the part label
        // is dropped — so parts are reconstructed by distinct reference value (order
        // of first appearance). Two parts sharing an identical reference value would
        // merge; a rare, acceptable reload caveat.
        hydrate(input) {
            if (!input || typeof input !== 'object') return;
            const s = (v) => (v === undefined || v === null ? '' : String(v));
            setUsl(s(input.usl));
            setLsl(s(input.lsl));
            if (Array.isArray(input.categories) && input.categories.length) {
                setCategoriesRaw(input.categories.join(', '));
            }
            const assessments = Array.isArray(input.assessments) ? input.assessments : [];
            // Prefer the persisted `part` (loss-free); older rows without it fall back
            // to grouping by distinct reference value (two same-value parts would merge).
            const hasPart = assessments.some((a) => a.part !== undefined && a.part !== null);
            const partKeyOf = (a) => (hasPart ? String(a.part) : String(a.referenceValue));
            const partIndexByKey = new Map();
            const opOrder = [];
            const refs = {};
            assessments.forEach((a) => {
                const key = partKeyOf(a);
                if (!partIndexByKey.has(key)) {
                    const idx = partIndexByKey.size + 1;
                    partIndexByKey.set(key, idx);
                    refs[idx] = a.referenceValue;
                }
                const op = String(a.operator);
                if (!opOrder.includes(op)) opOrder.push(op);
            });
            // The UI labels operators Op1..OpN; map persisted labels positionally.
            const opLabelByOrig = new Map(opOrder.map((o, i) => [o, `Op${i + 1}`]));
            const nextRatings = {};
            assessments.forEach((a) => {
                const pIdx = partIndexByKey.get(partKeyOf(a));
                const opLabel = opLabelByOrig.get(String(a.operator));
                nextRatings[`${pIdx}|${opLabel}`] = a.rating;
            });
            setNumPartsRaw(String(Math.max(1, partIndexByKey.size)));
            setNumOperatorsRaw(String(Math.max(2, opOrder.length)));
            setPartRefs(refs);
            setRatings(nextRatings);
        },
    }));

    if (guidedMode) {
        return (
            <GuidedEntry
                contextLabel="Attribute Analytic — Guided Rating"
                slots={guidedSlots}
                values={guidedValues}
                categories={gridCategories}
                onSubmitValue={handleGuidedSubmitValue}
                onClose={handleExitGuided}
                backLabel="Back to Grid"
                specLimits={{ usl, lsl }}
            />
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Spec limits + categories */}
            <Box>
                <Typography sx={{ ...sectionLabelSx, mb: 1 }}>Spec Limits &amp; Categories</Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ flexWrap: 'wrap' }}>
                    <TextField
                        label="LSL"
                        size="small"
                        sx={{ ...inputSx, width: { xs: '100%', sm: 130 } }}
                        value={lsl}
                        onChange={(e) => setLsl(e.target.value)}
                        disabled={disabled}
                        inputProps={{ inputMode: 'decimal' }}
                        placeholder="lower"
                    />
                    <TextField
                        label="USL"
                        size="small"
                        sx={{ ...inputSx, width: { xs: '100%', sm: 130 } }}
                        value={usl}
                        onChange={(e) => setUsl(e.target.value)}
                        disabled={disabled}
                        inputProps={{ inputMode: 'decimal' }}
                        placeholder="upper"
                    />
                    <TextField
                        label="Categories"
                        size="small"
                        sx={{ ...inputSx, flex: 1, minWidth: { xs: '100%', sm: 200 } }}
                        value={categoriesRaw}
                        onChange={(e) => handleCategoriesChange(e.target.value)}
                        disabled={disabled}
                        placeholder="Pass, Fail"
                        helperText="First category = conforming/accept call"
                        FormHelperTextProps={{ sx: { color: 'rgba(255,255,255,0.4)', mx: 0 } }}
                    />
                </Stack>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'block', mt: 0.5 }}>
                    At least one spec limit is required. Parts inside the limits are truly conforming; outside are truly defective.
                </Typography>
            </Box>

            {/* Config */}
            <Box>
                <Typography sx={{ ...sectionLabelSx, mb: 1 }}>Configuration</Typography>
                <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap' }}>
                    <TextField
                        label="Parts"
                        size="small"
                        sx={{ ...inputSx, width: 120 }}
                        value={numPartsRaw}
                        onChange={(e) => setNumPartsRaw(e.target.value)}
                        disabled={disabled}
                        inputProps={{ inputMode: 'numeric' }}
                    />
                    <TextField
                        label="Operators"
                        size="small"
                        sx={{ ...inputSx, width: 120 }}
                        value={numOperatorsRaw}
                        onChange={(e) => setNumOperatorsRaw(e.target.value)}
                        disabled={disabled}
                        inputProps={{ inputMode: 'numeric' }}
                    />
                </Stack>
            </Box>

            {/* Parts reference-value table */}
            <Box>
                <Typography sx={{ ...sectionLabelSx, mb: 1 }}>Parts — Reference Values</Typography>
                <CategoricalGrid
                    columns={partColumns}
                    rows={partRows}
                    onCellChange={handlePartCellChange}
                    disabled={disabled}
                    maxHeight={280}
                />
            </Box>

            {/* Ratings grid */}
            <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    <Typography sx={sectionLabelSx}>Operator Ratings</Typography>
                    <Tooltip title="Guided fullscreen entry — tap Pass/Fail through every row in order (mobile keypad / keyboard 1–N)">
                        <span>
                            <Button
                                size="small"
                                startIcon={<FullscreenIcon />}
                                onClick={handleEnterGuided}
                                disabled={disabled}
                                sx={{ fontSize: '0.7rem', color: 'rgba(144,202,249,0.7)', py: 0.25, px: 1 }}
                            >
                                Guided Entry
                            </Button>
                        </span>
                    </Tooltip>
                </Box>
                <CategoricalGrid
                    columns={ratingColumns}
                    rows={ratingRows}
                    onCellChange={handleRatingCellChange}
                    categories={gridCategories}
                    disabled={disabled}
                    maxHeight={420}
                />
            </Box>
        </Box>
    );
});

export default function AttributeAnalyticModule({ onBack }) {
    return (
        <MSAModulePage
            studyType="attributeAnalytic"
            title="Attribute Analytic Study"
            subtitle="Signal Detection"
            onBack={onBack}
            descriptionPlaceholder="e.g. Bore-gauge go/no-go vs CMM reference, 5 parts × 2 operators"
            helperText="Judges Pass/Fail calls against each part's true reference value to score detection performance."
        >
            {({ inputRef, disabled }) => <AttributeAnalyticInputs ref={inputRef} disabled={disabled} />}
        </MSAModulePage>
    );
}
